import { defineComponent, ref, computed, inject } from 'vue';
import { useLocalizationSupport } from '../../composables/localization-support/index.js';
import { useStoreModule } from '../../composables/store-module/index.js';
import { portfolioHeaderFactory } from '../portfolio-header/setup/index.js';
import HoistComponent from '../hoist-component/HoistComponent.vue.js';
import { portfolioSelectorFactory } from '../portfolio-selector/setup/index.js';
import { portfolioPositionsListFactory } from '../portfolio-positions-list/setup/index.js';
import { portfolioTransactionsListFactory } from '../portfolio-transactions-list/setup/index.js';
import { portfolioTransactionEditFactory } from '../portfolio-transaction-edit/setup/index.js';
import { portfolioTransactionDeleteFactory } from '../portfolio-transaction-delete/setup/index.js';
import { portfolioCreateFactory } from '../portfolio-create/setup/index.js';
import { portfolioEditFactory } from '../portfolio-edit/setup/index.js';
import { portfolioDeleteFactory } from '../portfolio-delete/setup/index.js';
import PortfolioOverview from './components/PortfolioOverview.vue.js';
import PortfolioAction from './components/PortfolioAction.vue.js';
import PortfolioSuggestTransaction from './components/PortfolioSuggestTransaction.vue.js';
import UiTabBar from '../ui-tab-bar/UiTabBar.vue.js';
import UiDropdownMenu from '../ui-dropdown-menu/UiDropdownMenu.vue.js';
import CloseButton from './components/CloseButton.vue.js';
import { newsListFactory } from '../news-list/setup/index.js';
import UiRenderNothing from '../ui-render-nothing/UiRenderNothing.vue.js';

var _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Portfolio",
  props: {
    locale: { type: null, required: true },
    tabs: { type: Array, required: false, default: () => [
      { key: "overview", label: "portfolio.tab.overview" },
      { key: "positions", label: "portfolio.tab.positions" },
      { key: "transactions", label: "portfolio.tab.transactions" },
      { key: "news", label: "portfolio.tab.news" }
    ] },
    additionalNewsCriteria: { type: null, required: false },
    suggesterProps: { type: null, required: false },
    newsArticleItemsPerPage: { type: Number, required: false, default: 5 },
    positionItemsPerPage: { type: Number, required: false, default: 5 },
    transactionItemsPerPage: { type: Number, required: false, default: 10 },
    defaultId: { type: null, required: false }
  },
  emits: ["store-error", "data-load", "on-portfolio-change", "positions-list-table-row-click", "overview-positions-table-row-click", "transactions-list-table-row-click", "news-list-item-click"],
  setup(__props, { emit: emits }) {
    var _a, _b;
    const props = __props;
    const id = ref((_a = props.defaultId) != null ? _a : null);
    const selectedTab = ref((_b = props.tabs[0]) != null ? _b : null);
    const errorBag = ref([]);
    const componentInputs = computed(() => ({
      id: id.value,
      newsCriteria: props.additionalNewsCriteria,
      language: props.locale
    }));
    const showNavigation = computed(() => {
      return props.tabs.length > 1;
    });
    const hasPortfolios = computed(() => {
      var _a2;
      return ((_a2 = numberOfPortfolios.value) != null ? _a2 : 0) > 0;
    });
    const newSearchCriteria = computed(() => {
      var _a2;
      return (_a2 = storeMeta.value) == null ? void 0 : _a2.newsListCriteria;
    });
    const numberOfPortfolios = computed(() => {
      var _a2;
      return (_a2 = storeMeta.value) == null ? void 0 : _a2.numberOfPortfolios;
    });
    const numberOfPositions = computed(() => {
      var _a2;
      return (_a2 = storeMeta.value) == null ? void 0 : _a2.numberOfPositions;
    });
    const numberOfTransactions = computed(() => {
      var _a2;
      return (_a2 = storeMeta.value) == null ? void 0 : _a2.numberOfTransactions;
    });
    const showNoPortfoliosAlert = computed(() => {
      return !hasPortfolios.value && !isLoadingStoreData.value && !hasStoreErrors.value;
    });
    const onPortfolioChange = (portfolioId) => {
      id.value = portfolioId;
      emits("on-portfolio-change", portfolioId);
    };
    const onError = (newErrorBag, callBack) => {
      errorBag.value = newErrorBag.map((error) => error.message);
      setTimeout(() => {
        errorBag.value = [];
      }, 5e3);
      if (callBack) {
        callBack();
      }
    };
    const changeTab = (tab) => {
      selectedTab.value = tab;
    };
    const onPortfolioCreate = (payload, callBack) => {
      if (payload) {
        onPortfolioChange(payload);
      }
      callBack();
    };
    const onPositionsListTableRowClick = (item) => {
      emits("positions-list-table-row-click", item);
    };
    const onOverviewPositionsTableRowClick = (item) => {
      emits("overview-positions-table-row-click", item);
    };
    const onTransactionsListTableRowClick = (item) => {
      emits("transactions-list-table-row-click", item);
    };
    const onNewsListItemClick = (item) => {
      emits("news-list-item-click", item);
    };
    const translator = inject("translator");
    const numberFormatter = inject("numberFormatter");
    const dateFormatter = inject("dateFormatter");
    const injectedStoreModule = inject("storeModule");
    const localizationSupport = useLocalizationSupport(props, { translator, numberFormatter, dateFormatter });
    const { translate } = localizationSupport;
    const storeModule = useStoreModule(
      { storeModule: injectedStoreModule },
      componentInputs,
      emits
    );
    const { storeMeta, isLoadingStoreData, hasStoreErrors } = storeModule;
    const PortfolioHeader = portfolioHeaderFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioHeader : void 0
    });
    const PortfolioSelector = portfolioSelectorFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioSelector : void 0
    });
    const PortfolioPositionsList = portfolioPositionsListFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioPositionsList : void 0
    });
    const PortfolioTransactionsList = portfolioTransactionsListFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioTransactionsList : void 0
    });
    const PortfolioTransactionEdit = portfolioTransactionEditFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioTransactionEdit : void 0
    });
    const PortfolioTransactionDelete = portfolioTransactionDeleteFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioTransactionDelete : void 0
    });
    const PortfolioCreate = portfolioCreateFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioCreate : void 0
    });
    const PortfolioEdit = portfolioEditFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioEdit : void 0
    });
    const PortfolioDelete = portfolioDeleteFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().portfolioDelete : void 0
    });
    const NewsList = newsListFactory({
      translator,
      numberFormatter,
      dateFormatter,
      storeModule: (injectedStoreModule == null ? void 0 : injectedStoreModule.getDependencies) ? injectedStoreModule.getDependencies().newsList : void 0
    });
    return { __sfc: true, props, emits, id, selectedTab, errorBag, componentInputs, showNavigation, hasPortfolios, newSearchCriteria, numberOfPortfolios, numberOfPositions, numberOfTransactions, showNoPortfoliosAlert, onPortfolioChange, onError, changeTab, onPortfolioCreate, onPositionsListTableRowClick, onOverviewPositionsTableRowClick, onTransactionsListTableRowClick, onNewsListItemClick, translator, numberFormatter, dateFormatter, injectedStoreModule, localizationSupport, translate, storeModule, storeMeta, isLoadingStoreData, hasStoreErrors, PortfolioHeader, PortfolioSelector, PortfolioPositionsList, PortfolioTransactionsList, PortfolioTransactionEdit, PortfolioTransactionDelete, PortfolioCreate, PortfolioEdit, PortfolioDelete, NewsList, HoistComponent, PortfolioOverview, PortfolioAction, PortfolioSuggestTransaction, UiTabBar, UiDropdownMenu, CloseButton, UiRenderNothing };
  }
});

export { _sfc_main as default };
